import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, FileVideo, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { useProfile } from "@/context/ProfileContext";
import { errorMessage } from "@/lib/api";
import { Field } from "@/components/music/FormBits";
import { FilePick } from "@/components/music/UploadForm";
import { VIDEO_CATEGORIES, MAX_VIDEO_BYTES } from "./videoConstants";

// Reads dimensions/duration and grabs a frame as a default thumbnail.
const inspectVideo = (file) => new Promise((resolve) => {
  const url = URL.createObjectURL(file);
  const v = document.createElement("video");
  v.muted = true; v.playsInline = true; v.preload = "auto"; v.src = url;
  let finished = false;
  const done = (frame) => { if (finished) return; finished = true; URL.revokeObjectURL(url); resolve({ width: v.videoWidth, height: v.videoHeight, duration: v.duration, frame }); };
  v.onloadedmetadata = () => { v.currentTime = Math.min(1, (v.duration || 2) / 2); };
  v.onseeked = () => {
    const c = document.createElement("canvas");
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    c.toBlob((b) => done(b && new File([b], "thumbnail.jpg", { type: "image/jpeg" })), "image/jpeg", 0.85);
  };
  v.onerror = () => done(null);
  setTimeout(() => done(null), 8000);
});

export const VideoUploadForm = () => {
  const { request, refresh } = useProfile();
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: "", category: "", description: "" });
  const [video, setVideo] = useState(null);
  const [meta, setMeta] = useState(null);
  const [thumb, setThumb] = useState(null);
  const [thumbIsAuto, setThumbIsAuto] = useState(false);
  const [preview, setPreview] = useState(null);
  const [progress, setProgress] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (!thumb) return setPreview(null);
    const url = URL.createObjectURL(thumb);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [thumb]);

  const pickVideo = async (f) => {
    if (!f) return;
    if (!/\.(mp4|m4v)$/i.test(f.name) && f.type !== "video/mp4") return toast.error("Choose an MP4 video");
    if (f.size > MAX_VIDEO_BYTES) return toast.error("Video must be under 200 MB");
    setVideo(f);
    const info = await inspectVideo(f);
    setMeta(info);
    if (info.frame && (!thumb || thumbIsAuto)) { setThumb(info.frame); setThumbIsAuto(true); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!video || !thumb) return toast.error("Add an MP4 video and a thumbnail");
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append("video", video); fd.append("thumbnail", thumb);
    if (meta?.width && meta?.height) { fd.append("client_width", meta.width); fd.append("client_height", meta.height); }
    if (Number.isFinite(meta?.duration)) fd.append("client_duration", meta.duration);
    setProgress(0);
    try {
      const v = await request("post", "/videos", fd, { onUploadProgress: (p) => setProgress(Math.round((p.loaded / (p.total || 1)) * 100)) });
      toast.success(v.is_short ? "Short published" : "Video published");
      refresh();
      navigate(v.is_short ? `/shorts?start=${v.id}` : `/watch/${v.id}`);
    } catch (err) { toast.error(errorMessage(err)); setProgress(null); }
  };

  const isShort = meta && meta.height > meta.width && meta.duration <= 180;
  const busy = progress !== null;
  return (
    <form data-testid="video-upload-form" onSubmit={submit} className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <FilePick testId="upload-video-input" accept="video/mp4,.mp4,.m4v,video/x-m4v" icon={FileVideo} label="Video (MP4, up to 200 MB)" file={video} onPick={pickVideo} />
        <FilePick testId="upload-thumbnail-input" accept="image/jpeg,image/png,image/webp" icon={ImagePlus} label="Thumbnail" file={thumb} preview={preview} onPick={(f) => { if (f) { setThumb(f); setThumbIsAuto(false); } }} />
      </div>
      {meta?.width > 0 && Number.isFinite(meta.duration) && (
        <p data-testid="upload-video-format" className="rounded-xl border border-white/10 bg-nw-surface/60 px-4 py-2.5 text-xs text-slate-400">
          {meta.width}×{meta.height} · {Math.round(meta.duration)}s — will appear in <span className="font-semibold text-white">{isShort ? "Shorts" : "Theater"}</span>
        </p>
      )}
      <Field label="Video title"><input data-testid="upload-video-title-input" required maxLength={100} value={form.title} onChange={set("title")} className="nw-input" placeholder="Neon Requiem (Official Video)" /></Field>
      <Field label="Category">
        <select data-testid="upload-video-category-select" required value={form.category} onChange={set("category")} className="nw-input appearance-none">
          <option value="" disabled>Choose a category</option>
          {VIDEO_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>
      <Field label="Description"><textarea data-testid="upload-video-description-input" rows={3} maxLength={2000} value={form.description} onChange={set("description")} className="nw-input resize-none" placeholder="Concept, AI tools used, credits..." /></Field>
      <button data-testid="upload-video-submit-button" disabled={busy} className="btn-red relative w-full overflow-hidden py-3.5 text-sm uppercase tracking-[0.2em] disabled:opacity-80">
        {busy && <span className="absolute inset-y-0 left-0 bg-white/20 transition-[width]" style={{ width: `${progress}%` }} />}
        <span className="relative">{busy ? <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> {progress < 100 ? `Uploading ${progress}%` : "Processing..."}</span> : "Publish video"}</span>
      </button>
    </form>
  );
};
